import React, { useEffect, useState } from "react";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import {
  TextField, 
  CircularProgress,  
  capitalize,
  FormControl,
  Select,
  MenuItem,
 } from '@mui/material';
import { fetchUserById } from "../../../redux/actions/users.actions";
import { assignRole, fetchRoles } from "../../../redux/actions/roles.actions";
import { ModalProps } from "../Interfaces/Modals";
import { updateUser } from "../../../redux/actions/user.actions";
import { fetchDealership } from "../../../redux/actions/dealership.actions";
import DealershipSelect from "../UserSelect/DealershipSelect";
import { canSelectUser } from "../Interfaces/UserRoleManagement";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import ModalShell, { ModalLoading } from "./ModalShell";

const EditUserModal: React.FC<ModalProps> = ({open, setOpen, object_id}) => {
  const user_id = object_id??null;

  const dispatch: AppDispatch = useDispatch();
  const {loading, error, data:user} = useSelector((state: RootState) => state.usersById);
  const {data:roles, loading: rolesLoading, error: rolesError} = useSelector((state: RootState) => state.roles);
  const {data:dealership, loading: dealershipLoading} = useSelector((state: RootState) => state.dealership);
  const {data:currentUser} = useSelector((state: RootState) => state.user); 
  
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [updateInfo, setUpdateInfo] = useState<any>(null);
  const [role, setRole] = useState<any>(null);
  const [selectedDealership, setSelectedDealership] = useState<any>(null);
  const [lastFetchedDealershipId, setLastFetchedDealershipId] = useState<string | null>(null);
  const [hasShownWarning, setHasShownWarning] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);

  const canEditUser = usePermissionCheck(['update_user']).canAccess || currentUser?.id===user_id;
  const canAssignAdmin = usePermissionCheck(['assign_admin_role']).canAccess;
  const canAssignDeveloper = usePermissionCheck(['assign_developer_role']).canAccess;
  const canAssignOwner = usePermissionCheck(['assign_corporate_role']).canAccess;
  const canAssignDealer = usePermissionCheck(['assign_owner_role']).canAccess;
  const canAssignStaff = usePermissionCheck(['assign_dealer_role', 'assign_technician_role', 'assign_sales_role', 'assign_customer_role']).canAccess;

  const handleClose = () => {
    setSubmitted(false);
    setHasShownWarning(false);
    setUpdateInfo(null);
    setRole(null);
    setSelectedDealership(null);
    setLastFetchedDealershipId(null);
    setOpen(false);
    setSubmitting(false);
    setEmailError(null);
    setUsernameError(null);
  };

  useEffect(() => {
    if (open) {
      if (user_id && user_id!==user?.id) {
        dispatch(fetchUserById(user_id));
      }

      if (
        user?.dealership_id
        && user?.dealership_id !== lastFetchedDealershipId
      ) {
        dispatch(fetchDealership(user.dealership_id));
        setLastFetchedDealershipId(user.dealership_id);
      }

      if (!roles) {
        dispatch(fetchRoles(''));
      }
    }
    if (!open || user?.id !== user_id) {
      setLastFetchedDealershipId(null);
    }
  }, [dispatch, user_id, open, user, dealership, roles, lastFetchedDealershipId]);

  useEffect(() => {
    if (selectedDealership) {
      setUpdateInfo({...updateInfo,dealership_id:selectedDealership.id});
    }
  }, [selectedDealership]);

  const handleSave = async () => {
    setSubmitted(true);
    setSubmitting(true);

    if (role) {
      await dispatch(assignRole({role_id:role,users:[user]}))
    }

    if (updateInfo) {
      await dispatch(updateUser({...updateInfo,uuid:user?.id??''}))
    }

    if (error) {
      if (error?.email) {
        setEmailError(error.email);
      }
      if (error?.username) {
        setUsernameError(error.username);
      }
    }

    setSubmitting(false);
  }

  const sortRoles = () => {
    let new_array = [...roles];
    const sorted_array = ['developer','admin','corporate','owner','dealer','sales','technician','customer'];

    new_array.sort((a, b) => {
      return sorted_array.indexOf(a.name) - sorted_array.indexOf(b.name);
    });
    return new_array;
  }

  const handleRoleChange = (newRole: string) => {
    const isCorporateUser = user?.roles[0]?.name === 'corporate';
    if (isCorporateUser && newRole !== user?.roles[0]?.id && !hasShownWarning) {
      if (window.confirm('Warning: Changing this user\'s role will unlink them as a corporation from all their dealerships. Do you want to continue?')) {
        setRole(newRole);
        setHasShownWarning(true);
      }
    } else {
      setRole(newRole);
    }
  };

  const isLoading = Boolean(loading || rolesLoading || dealershipLoading) && !submitting;
  const isError = Boolean((error && typeof error === 'string') || rolesError) && submitted;
  const isSuccess = !(error || rolesError) && submitted;
  const showForm = !isLoading && !submitting && !isError && !isSuccess;

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      size={showForm ? "md" : "sm"}
      busy={submitting}
      tone={submitting || isLoading ? undefined : isError ? "error" : isSuccess ? "success" : undefined}
      title={
        submitting ? "Saving…"
        : isLoading ? "Loading…"
        : isError ? "An error occurred"
        : isSuccess ? "User updated"
        : <>Edit <strong>{user?.name}</strong></>
      }
      actions={submitting || isLoading ? undefined : isError || isSuccess ?
        <button className="btn btn-primary" onClick={handleClose}>OK</button>
        :
        <>
          <button className="btn" onClick={handleClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading || rolesLoading || (!updateInfo && !role) || submitting}>
            {loading || rolesLoading || submitting ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      {submitting || isLoading ?
        <ModalLoading/>
      : isError ?
        <p>{rolesError ?? error}</p>
      : isSuccess ?
        <p>User information was successfully updated.</p>
      :
        <>
          <TextField
            id="username"
            label="Username"
            variant="outlined"
            fullWidth
            defaultValue={updateInfo?.username ?? user?.username}
            onChange={(e)=>setUpdateInfo({...updateInfo,username:e.target.value})}
            disabled={!canEditUser}
            autoComplete="off"
            error={!!usernameError}
            helperText={usernameError}
          />

          <TextField
            id="email"
            label="Email"
            variant="outlined"
            fullWidth
            defaultValue={updateInfo?.email ?? user?.email}
            onChange={(e)=>setUpdateInfo({...updateInfo,email:e.target.value})}
            disabled={!canEditUser}
            autoComplete="off"
            error={!!emailError}
            helperText={emailError}
          />

          <TextField
            id="name"
            label="Name"
            variant="outlined"
            fullWidth
            defaultValue={updateInfo?.name ?? user?.name}
            onChange={(e)=>setUpdateInfo({...updateInfo,name:e.target.value})}
            disabled={!canEditUser}
            autoComplete="off"
          />

          {[canAssignDeveloper,canAssignAdmin,canAssignOwner,canAssignDealer,canAssignStaff].includes(true) && roles?.length>0 && canSelectUser(currentUser?.roles ? currentUser?.roles[0]?.name : null, user?.roles ? user?.roles[0]?.name : null, currentUser?.id, user?.id) &&
            <div className="dlg-section">
              <span className="dlg-label" id="role-select-label">Role</span>
              <FormControl fullWidth>
                <Select
                    labelId="role-select-label"
                    id="role-select"
                    value={role ?? (user?.roles ? user?.roles[0]?.id : "")}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    disabled={rolesLoading}
                >
                  {rolesLoading ? (
                    <MenuItem value=""><CircularProgress size={"24px"} /></MenuItem>
                  ) : (
                    sortRoles().map((role: any) =>
                      ((
                        (role?.name === 'developer') && canAssignDeveloper)
                        || ((role?.name === 'admin' || role?.name === 'corporate') && canAssignAdmin)
                        || ((role?.name === 'owner') && canAssignOwner)
                        || ((role?.name === 'dealer') && canAssignDealer)
                        || ((role?.name === 'sales' || role?.name === 'technician' || (role?.name === 'customer')) && canAssignStaff)
                      ) && (
                        <MenuItem key={role?.id} value={role?.id}>{capitalize(role?.name)}</MenuItem>
                      )
                    )
                  )}
                </Select>
              </FormControl>
            </div>}

          {[canEditUser].includes(true) &&
            <div className="dlg-section">
              <span className="dlg-label">Dealership assignment</span>
              <div className="dlg-current">
                <span>Current dealership</span>
                <strong>{user?.dealership_id ? dealership?.name : 'None'}</strong>
              </div>
              <DealershipSelect setSelected={setSelectedDealership} selected={selectedDealership} current={user?.dealership_id ? dealership : null} />
              {selectedDealership && (
                <p><strong>Will be assigned to:</strong> {selectedDealership.name} (ID: {selectedDealership.id})</p>
              )}
            </div>}
        </>
      }
    </ModalShell>
  );
};

export default EditUserModal;
